"use client"

import { useState } from "react"
import { Filter, X, ChevronDown, Calendar, FileText, Building, Search } from "lucide-react"
import TwoColumnSwitch from "../utils/TwoColumnSwitch"
import ButtonLoading from "../User/ButtonLoading"

const NewReleaseFilter = ({
  industries,
  searchTerm,
  setSearchTerm,
  selectedIndustry,
  setSelectedIndustry,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  showFilters,
  setShowFilters,
  executeSearch,
  twoColumnSwitchOptions,
  handleNewReleaseCountrySelect,
  newReleaseCountry,
  titleSearchTerm,
  setTitleSearchTerm,
  isFiltering,
  setIsFiltering,
}) => {
  const [inputTerm, setInputTerm] = useState("")
  const [inputTitleTerm, setInputTitleTerm] = useState("")

  const resetFilters = () => {
    setSearchTerm("")
    setInputTerm("")
    setTitleSearchTerm("")
    setInputTitleTerm("")
    setSelectedIndustry()
    setStartDate("")
    setEndDate("")
  }

  // Enterキーで検索実行
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault()
      executeSearch(inputTerm, inputTitleTerm)
    }
  }

  // 検索ボタンクリック時
  const handleSearch = () => {
    executeSearch(inputTerm, inputTitleTerm)
    // APIリクエストが完了したら親コンポーネントからコールバックで通知を受ける
  }

  const handleInputDelete = () => {
    setInputTerm("")
    executeSearch("", inputTitleTerm)
  }

  const handleInputTitleDelete = () => {
    setInputTitleTerm("")
    executeSearch(inputTerm, "")
  }

  return (
    <div className="mt-2">
      <div className="flex flex-wrap items-center gap-4">
        {/* 検索フィールド部分 */}
        <div className="flex flex-grow flex-wrap gap-2 max-w-3xl">
          {/* 企業名検索ボックス */}
          <div className="relative flex-grow min-w-[200px]">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <Building className="w-4 h-4 text-gray-500" />
            </div>
            <input
              type="text"
              value={inputTerm}
              onChange={(e) => setInputTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="企業名"
              className="w-full py-2 pl-10 pr-4 border border-gray-300 rounded-lg focus:ring-1 focus:ring-gray-200 focus:border-gray-400 outline-none"
            />
            {inputTerm && (
              <button onClick={handleInputDelete} className="absolute inset-y-0 right-0 flex items-center pr-3">
                <X className="w-4 h-4 text-gray-500 hover:text-gray-700" />
              </button>
            )}
          </div>

          {/* タイトル検索ボックス */}
          <div className="relative flex-grow min-w-[200px]">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <FileText className="w-4 h-4 text-gray-500" />
            </div>
            <input
              type="text"
              value={inputTitleTerm}
              onChange={(e) => setInputTitleTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="タイトル"
              className="w-full py-2 pl-10 pr-4 border border-gray-300 rounded-lg focus:ring-1 focus:ring-gray-200 focus:border-gray-400 outline-none"
            />
            {inputTitleTerm && (
              <button onClick={handleInputTitleDelete} className="absolute inset-y-0 right-0 flex items-center pr-3">
                <X className="w-4 h-4 text-gray-500 hover:text-gray-700" />
              </button>
            )}
          </div>

          {/* 共通の検索ボタン */}
          <button
            onClick={handleSearch}
            disabled={isFiltering}
            className={`px-4 py-2 ${isFiltering ? "bg-gray-400" : "bg-gray-600 hover:bg-gray-700"} text-white font-medium rounded-lg transition-colors flex items-center gap-2`}
          >
              {isFiltering ? <ButtonLoading /> : <><Search className="w-4 h-4" />検索</>}
          </button>
        </div>

        {/* フィルターボタン */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
        >
          <Filter className="w-4 h-4" />
          フィルター
          <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? "rotate-180" : ""}`} />
        </button>

        {/* フィルターが適用されている場合のリセットボタン */}
        {(selectedIndustry || startDate || endDate || searchTerm || titleSearchTerm) && (
          <button
            onClick={resetFilters}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100"
          >
            <X className="w-3.5 h-3.5" />
            フィルターをクリア
          </button>
        )}
      </div>

      {/* 展開されるフィルターオプション */}
      {showFilters && (
        <div className="p-4 mb-4 bg-gray-50 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* 日本/米国 */}
            <div className="mt-[24px]">
              <TwoColumnSwitch
                options={twoColumnSwitchOptions}
                onChange={handleNewReleaseCountrySelect}
                defaultId={newReleaseCountry}
              />
            </div>
            {/* 業種フィルター */}
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">業種</label>
              <select
                value={selectedIndustry || ""}
                onChange={(e) => {
                  // const selected = industries.find((industry) => industry.id === Number(e.target.value));
                  setSelectedIndustry(Number(e.target.value))
                }}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              >
                {industries.length > 0 && (
                  <>
                    <option value="">すべての業種</option>
                    {industries.map((industry) => (
                      <option key={industry.id} value={industry.id}>
                        {industry.industry_name}
                      </option>
                    ))}
                  </>
                )}
              </select>
            </div>
          </div>

          {/* カスタム期間選択 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="flex items-center gap-2 mb-2 text-sm font-medium text-gray-700">
                <Calendar className="w-4 h-4" />
                開始日
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value)
                }}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
            </div>
            <div>
              <label className="flex items-center gap-2 mb-2 text-sm font-medium text-gray-700">
                <Calendar className="w-4 h-4" />
                終了日
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value)
                }}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default NewReleaseFilter
